import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Lembra wordmark — single source of truth across every surface.
 *
 * MS49 unification:
 *   Pre-MS49 this component wrapped the logo SVG in a navy rounded
 *   pill on light surfaces. The pill was inconsistent with both:
 *     • the catering platform's clean SVG-only treatment
 *     • the new ConciergeLayout's typographic Newsreader wordmark
 *   Three different logo treatments lived in the same product
 *   family, and Lucky correctly called it out as "the Lembra logo
 *   is also incorrect inside the registered dashboard."
 *
 *   This rewrite drops the pill and picks the right SVG variant for
 *   the surface (matching catering's LembraLogo pattern):
 *     • `onDark={false}` (default) → renders `/logo-navy.svg` cleanly
 *     • `onDark={true}` → renders `/logo.svg` (butter on dark bg)
 *   Same API surface — every existing `<Logo size="sm" />` /
 *   `<Logo onDark />` caller continues to work without changes.
 *
 * Click behaviour:
 *   Routes to /dashboard when the user is signed in, else /. On the
 *   landing page (already at /), clicking smooth-scrolls to top.
 */

interface LogoProps {
  size?: "sm" | "md" | "lg";
  /**
   * True when the logo sits on a dark background (e.g. the navy
   * sidebar in BakeryLayout, the navy footer band on landing).
   * The butter SVG is used so the wordmark reads against the dark.
   */
  onDark?: boolean;
  className?: string;
}

const sizeMap: Record<NonNullable<LogoProps["size"]>, string> = {
  sm: "h-6",
  md: "h-8",
  lg: "h-10",
};

export function Logo({ size = "md", onDark = false, className }: LogoProps) {
  const { user, session, loading } = useAuth();
  const logoDestination = !loading && session && user ? "/dashboard" : "/";
  const handleClick = () => {
    if (window.location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // MS49: pick the SVG variant by surface tone instead of wrapping
  // in a coloured pill. logo-navy.svg = navy fill (for light surfaces);
  // logo.svg = butter fill (for dark surfaces).
  const src = onDark ? "/logo.svg" : "/logo-navy.svg";

  return (
    <Link
      to={logoDestination}
      onClick={handleClick}
      aria-label="Lembra, home"
      className={cn("inline-flex items-center cursor-pointer", className)}
    >
      <img
        src={src}
        alt="Lembra"
        className={cn(sizeMap[size], "w-auto block")}
        draggable={false}
      />
    </Link>
  );
}
